import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router";
import Client, { ApiError } from "~/apiClient";
import { type UserProfile } from "~/schemas/user";
import { identify, trackSignIn, trackSignOut, reset } from "~/analytics";

type AuthContextType = {
  user: UserProfile | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (redirectTo: string | null) => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  let location = useLocation();

  const client = new Client(false, false);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const user = await client.userProfile();
      setUser(user);
      identify(user);
      trackSignIn(user);
    } catch (error) {
      setLoading(false);
      if (
        error instanceof ApiError &&
        (error.statusCode === 401 || error.statusCode === 403)
      ) {
        await client.anonLogin();
        const user = await client.userProfile();
        setUser(user);
      } else {
        throw error;
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const login = (redirectTo: string | null) => {
    setLoading(true);
    let redirectUrl = redirectTo || location.pathname;
    const backendUrl = `${
      import.meta.env.VITE_BACKEND_HOST_FOR_REDIRECTS
    }/api/auth/sessions?redirect_to=${encodeURIComponent(redirectUrl)}`;
    window.location.href = backendUrl;
    setLoading(false);
  };

  const logout = async () => {
    await client.logout();
    trackSignOut(user);
    reset();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user && !user.is_anon,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
